import React, { ReactNode } from 'react';
import { MsalProvider as MsalReactProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication, EventType, EventMessage } from '@azure/msal-browser';
import { msalConfig, graphScopes } from './authConfig';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Register event callbacks
msalInstance.addEventCallback((event: EventMessage) => {
  // Handle redirect response
  if (event.eventType === EventType.LOGIN_SUCCESS) {
    // Removed console.log about successful login
  }
  // Handle errors
  if (event.eventType === EventType.LOGIN_FAILURE) {
    // Removed console.error about login failure
  }
});

interface MsalProviderProps {
  children: ReactNode;
}

/**
 * MSAL Provider component that wraps the application with Microsoft Authentication
 */
export const MsalProvider: React.FC<MsalProviderProps> = ({ children }) => {
  return (
    <MsalReactProvider instance={msalInstance}>
      {children}
    </MsalReactProvider>
  );
};

/**
 * Hook to get an access token for Microsoft Graph API
 * @returns A function that acquires an access token
 */
export const useGraphToken = () => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  const getAccessToken = async (): Promise<string> => {
    // If user is not authenticated, try silent login
    if (!isAuthenticated || accounts.length === 0) {
      try {
        // Try silent login first
        const loginResponse = await instance.ssoSilent({
          scopes: graphScopes.scopes,
          account: accounts[0] || undefined,
        });
        return loginResponse.accessToken;
      } catch (error) {
        // Removed console.log about silent login failure
        try {
          // If silent login fails, try popup login
          const loginResponse = await instance.loginPopup({
            scopes: graphScopes.scopes,
          });
          return loginResponse.accessToken;
        } catch (popupError) {
          // Removed console.error about popup login failure
          throw popupError;
        }
      }
    }

    // User is authenticated, acquire token silently
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        scopes: graphScopes.scopes,
        account: accounts[0] || undefined,
      });
      return tokenResponse.accessToken;
    } catch (error) {
      // Removed console.error about token acquisition failure
      // If silent token acquisition fails, try popup
      try {
        const tokenResponse = await instance.acquireTokenPopup({
          scopes: graphScopes.scopes,
        });
        return tokenResponse.accessToken;
      } catch (popupError) {
        // Removed console.error about popup token acquisition failure
        throw popupError;
      }
    }
  };

  return { getAccessToken };
};
